import _ from 'lodash';
import { WEBSOCKET_VIRTUAL_EVENT_COMMAND, ACTIVITY_TYPE, CAMPAIGN_TYPE } from '../virtual-event.enums';
import { Store, select } from '@ngrx/store';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { updateVirtualEventData, updateVirtualEventCurrentItem, updateVirtualEventDataOptionInfo, loadVirtualEvent } from '../features/data/data.actions';
import { updateVirtualEventSummary } from '../features/summary/summary.actions';
import { updateVirtualEventActivity } from '../features/activity/activity.actions';
import { updateCampaign, loadEventCampaignList } from 'app/campaign/features/campaign.actions';
import { retryWhen, tap, delayWhen, first, takeUntil } from 'rxjs/operators';
import { timer, Subscription, Subject } from 'rxjs';
import { ToastService } from 'app/toast/services/toast.service';
import { SharedService } from '../services/shared.service';
import * as ActivityActions from 'app/virtual-event/features/activity/activity.actions';
import { selectVirtualEventSelectedCampaign } from '../features/data/data.selectors';
import { TransformService } from 'app/core/services/transform.service';
import { AddChatMessage, WSDeleteChatMessage } from 'app/chat/features/chat.actions';

export class WebSocketModel {
    public subject: WebSocketSubject<any> = null;
    public connection: Subscription = null;
    public takeUntilSubject: Subject<any> = new Subject();

    constructor(
        private id: string,
        private store: Store<any>,
        private toastService: ToastService,
        private sharedService: SharedService,
        private transformService: TransformService
    ) {
      const protocol = window.location.protocol.toLowerCase() == "https:" ? "wss": "ws";
      this.subject = webSocket({
        url: `${protocol}://${window.location.hostname}/ws/connect?eventId=${id}&mode=admin`
      });
      this.connection = this.subject.pipe(
        retryWhen(errors => {
          return errors.pipe(
            tap(() => {
              return this.toastService.show('danger', '[Web Socket] Try to reconnect', {delay: 10000});
            }),
            delayWhen(() => timer(10000))
          );
        }),
        takeUntil(this.takeUntilSubject)
      ).subscribe((value: any) => {
        let body = JSON.parse(value.body);
        body = this.transformService.transform(body);
        const actionTime = Math.round(new Date().getTime()/1000);
        switch(value.type) {
          case WEBSOCKET_VIRTUAL_EVENT_COMMAND.UPDATE_CAMPAIGN:
          case WEBSOCKET_VIRTUAL_EVENT_COMMAND.UPDATE_RUN_STATUS:
            const { virtualEventInfo, optionInfo } = body;
            this.store.dispatch({
              type: updateVirtualEventData.type,
              payload: virtualEventInfo
            });

            if (optionInfo && optionInfo.name) {
              const liveItemChangedAction = ActivityActions.updateVirtualEventActivity({
                payload: {
                  type: ACTIVITY_TYPE.CAMPAIGN_CHANGE,
                  actionTime,
                  name: optionInfo.name
                }
              });
              this.store.dispatch(liveItemChangedAction);
            }
            break;
          case WEBSOCKET_VIRTUAL_EVENT_COMMAND.UPDATE_BID:
            this.store.dispatch({
              type: updateVirtualEventCurrentItem.type,
              payload: {
                currentItem: body
              }
            });
            break;
          case WEBSOCKET_VIRTUAL_EVENT_COMMAND.UPDATE_TOTAL:
            const payloadUpdateTotal = body;
            this.store.dispatch({
              type: updateVirtualEventSummary.type,
              payload: body
            });
            _.forEach(payloadUpdateTotal.byCampaign, ({ campaignData: payload }) => {
              this.store.dispatch({
                type: updateCampaign.type,
                payload
              });
            });
            this.store.dispatch({
              type: updateVirtualEventDataOptionInfo.type,
              payload: payloadUpdateTotal.byCampaign
            });
            break;
          case WEBSOCKET_VIRTUAL_EVENT_COMMAND.RECENT_ACTIVITY:
            const payloadRecentActivity = body;
            this.store.pipe(
              select(selectVirtualEventSelectedCampaign),
              first()
            ).subscribe(campaign => {
              if(payloadRecentActivity.anonymousDonor) {
                if(_.isEqual(payloadRecentActivity.type, CAMPAIGN_TYPE.DONATE)) {
                  payloadRecentActivity.donor.name = 'Anonymous';
                }
              }
              this.store.dispatch({
                type: updateVirtualEventActivity.type,
                payload: {
                  ...payloadRecentActivity,
                  campaign
                }
              });
            });
            break;
          case WEBSOCKET_VIRTUAL_EVENT_COMMAND.SEND_MESSAGE:
            this.store.dispatch({
              type: AddChatMessage.type,
              payload: body
            });
            break;
          case WEBSOCKET_VIRTUAL_EVENT_COMMAND.WINNING_BID:
            const { winningBid: { userInfo } } = body;
            const activityAction = ActivityActions.updateVirtualEventActivity({
              payload: {
                type: ACTIVITY_TYPE.DECLARE_WINNER,
                actionTime,
                name: userInfo.name
              }
            });
            this.store.dispatch(activityAction);
            this.store.dispatch({
              type: loadVirtualEvent.type,
              payload: this.sharedService.id
            });
            this.store.dispatch({
              type: loadEventCampaignList.type,
              payload: this.sharedService.token
            });
            break;
          case WEBSOCKET_VIRTUAL_EVENT_COMMAND.WINNING_LAST_HERO:
            const { campaignInfo, winner: { name } } = body;
            this.store.dispatch({
              type: updateCampaign.type,
              payload: campaignInfo
            });
            this.store.dispatch({
              type: updateVirtualEventActivity.type,
              payload: {
                type: ACTIVITY_TYPE.DECLARE_LAST_HERO,
                actionTime: Math.round(new Date().getTime()/1000),
                name
              }
            });
            break;
          case WEBSOCKET_VIRTUAL_EVENT_COMMAND.UPDATE_CAMPAIGN_LIST:
            const { eventSummary, auctionCampaign } = body;
            this.store.dispatch({
              type: updateVirtualEventSummary.type,
              payload: eventSummary
            });
            this.store.dispatch({
              type: updateCampaign.type,
              payload: auctionCampaign
            });

            const silentAuctionClosedAction = ActivityActions.updateVirtualEventActivity({
              payload: {
                type: ACTIVITY_TYPE.SILENT_AUCTION_CLOSED,
                actionTime,
                name: auctionCampaign.name
              }
            });
            this.store.dispatch(silentAuctionClosedAction);
            break;
          case WEBSOCKET_VIRTUAL_EVENT_COMMAND.SHOW_LIVE_AUCTION_MULTI:
            this.store.dispatch({
              type: updateVirtualEventData.type,
              payload: body.virtualEventInfo
            });
            this.toastService.show('success', `[Sell More]: Amount ${body.amount}`, {delay: 10000});
            
            const sellMoreActivityAction = ActivityActions.updateVirtualEventActivity({
              payload: {
                type: ACTIVITY_TYPE.SELL_MORE,
                actionTime: Math.round(new Date().getTime()/1000),
                amount: body.amount,
                name: body.optionInfo.name
              }
            });
            this.store.dispatch(sellMoreActivityAction);
            break;
          case WEBSOCKET_VIRTUAL_EVENT_COMMAND.PRESENTATION_MODE_FULL_SCREEN_VIDEO:
            const { eventInfo, newMode } = body;
            _.set(eventInfo, 'currentItem.presentationMode', newMode);
            this.store.dispatch({
              type: updateVirtualEventData.type,
              payload: eventInfo
            });
            break;
          case WEBSOCKET_VIRTUAL_EVENT_COMMAND.DELETE_CHAT_ITEM:
              this.store.dispatch({
                type: WSDeleteChatMessage.type,
                payload: body
              });
              break;
        }
        window['orgSignIn'].extendCookieLife();
      });
    }
}
